import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Mail, Phone, MapPin, MessageSquare, Linkedin, Github, Contact, MessageSquareMore } from 'lucide-react';
import { toast } from 'sonner';

const AdmContact = () => {
  const { portfolioData, updateHero } = usePortfolio();
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    github: '',
    whatsapp: '',
    formMessage: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Initialize contact info from portfolioData
  useEffect(() => {
    if (portfolioData?.hero) {
      setContactInfo({
        email: portfolioData?.hero?.email || '',
        phone: portfolioData?.hero?.phone || '',
        whatsapp: portfolioData?.hero?.whatsapp || '',
        location: portfolioData?.hero?.location || '',
        linkedin: portfolioData?.hero?.linkedin || '',
        github: portfolioData?.hero?.github || '',
        formMessage: portfolioData?.hero?.formMessage || 'Merci pour votre intérêt ! N\'hésitez pas à me contacter pour discuter de vos projets.'
      });
    }
  }, [portfolioData]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const heroData = {
        ...portfolioData?.hero,
        email: contactInfo.email,
        phone: contactInfo.phone,
        whatsapp: contactInfo.whatsapp,
        location: contactInfo.location,
        linkedin: contactInfo.linkedin,
        github: contactInfo.github,
        formMessage: contactInfo.formMessage
      };
      
      const result = await updateHero(heroData);
      if (result?.success) {
        toast.success('Contact modifié avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la sauvegarde des informations de contact');
      }
    } catch (error) {
      // console.error('Error saving contact information:', error);
      toast.error('Erreur lors de la sauvegarde des informations de contact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (portfolioData?.hero) {
      setContactInfo({
        email: portfolioData.hero.email || '',
        phone: portfolioData.hero.phone || '',
        whatsapp: portfolioData.hero.whatsapp || '',
        location: portfolioData.hero.location || '',
        linkedin: portfolioData.hero.linkedin || '',
        github: portfolioData.hero.github || '',
        formMessage: portfolioData.hero.formMessage || 'Merci pour votre intérêt ! N\'hésitez pas à me contacter pour discuter de vos projets.'
      });
      toast.success('Formulaire réinitialisé');
    }
  };

  const handleChange = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Contact className="w-6 h-6 mr-2" />
          Gestion du Contact
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Coordonnées Principales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center mb-1">
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="votre.email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="flex items-center mb-1">
                <Phone className="w-4 h-4 mr-1" />
                Téléphone
              </Label>
              <Input
                id="phone"
                value={contactInfo.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+33 1 23 45 67 89"
              />
            </div>
            <div>
              <Label htmlFor="location" className="flex items-center mb-1">
                <MapPin className="w-4 h-4 mr-1" />
                Localisation
              </Label>
              <Input
                id="location"
                value={contactInfo.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Ville, Pays"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Réseaux Sociaux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="whatsapp" className="flex items-center mb-1">
                <MessageSquareMore className="w-4 h-4 mr-1" />
                WhatsApp
              </Label>
              <Input
                id="whatsapp"
                value={contactInfo.whatsapp}
                onChange={(e) => handleChange('whatsapp', e.target.value)}
                placeholder="https://wa.me/1234567890"
              />
            </div>
            <div>
              <Label htmlFor="linkedin" className="flex items-center mb-1">
                <Linkedin className="w-4 h-4 mr-1" />
                LinkedIn
              </Label>
              <Input
                id="linkedin"
                value={contactInfo.linkedin}
                onChange={(e) => handleChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/username"
              />
            </div>
            <div>
              <Label htmlFor="github" className="flex items-center mb-1">
                <Github className="w-4 h-4 mr-1" />
                GitHub
              </Label>
              <Input
                id="github"
                value={contactInfo.github}
                onChange={(e) => handleChange('github', e.target.value)}
                placeholder="https://github.com/username"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Message du Formulaire
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="formMessage" className="flex items-center mb-1">Message d'accueil du formulaire</Label>
              <Textarea
                id="formMessage"
                value={contactInfo.formMessage}
                onChange={(e) => handleChange('formMessage', e.target.value)}
                placeholder="Message d'accueil pour les visiteurs"
                rows={4}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Aperçu des Informations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <Mail className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <h4 className="font-medium">Email</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{contactInfo.email}</p>
            </div>
            <div className="text-center">
              <Phone className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <h4 className="font-medium">Téléphone</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{contactInfo.phone}</p>
            </div>
            <div className="text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <h4 className="font-medium">Localisation</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{contactInfo.location}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdmContact;